import React, { useState } from 'react';
import type { ReviewItem, ReviewRating } from '../../types/review.types';
import { playWordPronunciation } from '../../utils/audio';

interface ReviewCardProps {
  item: ReviewItem;
  isRevealed: boolean;
  onReveal: () => void;
  onRate: (rating: ReviewRating) => void;
  isSubmitting: boolean;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({
  item,
  isRevealed,
  onReveal,
  onRate,
  isSubmitting,
}) => {
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  const handlePlayAudio = (e: React.MouseEvent) => {
    e.stopPropagation();
    playWordPronunciation(
      item.word,
      item.audioUrl,
      () => setIsPlayingAudio(true),
      () => setIsPlayingAudio(false),
    );
  };

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4">
      {/* ─── CARD CONTAINER ─────────────────────────────────── */}
      <div
        id="review-card-container"
        onClick={() => {
          if (!isRevealed) onReveal();
        }}
        className={`relative min-h-[380px] rounded-3xl p-6 sm:p-8 flex flex-col justify-between transition-all duration-300
                    border backdrop-blur-xl select-none ${
                      isRevealed
                        ? 'bg-surface-900/95 border-primary-500/30 shadow-2xl shadow-primary-950/40'
                        : 'bg-gradient-to-br from-surface-800/90 to-surface-900/95 border-white/10 shadow-xl cursor-pointer hover:border-primary-500/40 hover:shadow-2xl'
                    }`}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              id="review-status-badge"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                item.learning.reviewCount === 0
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  : 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  item.learning.reviewCount === 0 ? 'bg-amber-400' : 'bg-primary-400'
                }`}
              />
              {item.learning.reviewCount === 0
                ? 'New Word'
                : `Review #${item.learning.reviewCount}`}
            </span>

            {item.learning.intervalDays > 0 && (
              <span className="text-xs text-slate-400 font-medium hidden sm:inline">
                Interval: {item.learning.intervalDays}d
              </span>
            )}
          </div>

          <button
            type="button"
            id="play-review-audio-btn"
            onClick={handlePlayAudio}
            title={`Listen to pronunciation of "${item.word}"`}
            aria-label={`Listen to ${item.word}`}
            className={`p-2.5 rounded-full text-white transition-all transform active:scale-95 ${
              isPlayingAudio
                ? 'bg-primary-500 ring-2 ring-primary-400 shadow-lg shadow-primary-500/30'
                : 'bg-surface-700/80 hover:bg-primary-600 border border-white/10'
            }`}
          >
            <svg
              className={`w-5 h-5 ${isPlayingAudio ? 'animate-pulse' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
            </svg>
          </button>
        </div>

        {/* Word Center Display */}
        <div className="text-center py-6">
          <h2
            id="review-card-word"
            className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-2"
          >
            {item.word}
          </h2>
          {item.ipa && (
            <p
              id="review-card-ipa"
              className="text-lg sm:text-xl font-mono text-primary-400 font-medium"
            >
              {item.ipa}
            </p>
          )}
        </div>

        {/* Revealed Content Details */}
        {isRevealed ? (
          <div className="space-y-4 pt-4 border-t border-white/10 animate-fade-in">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                Vietnamese Meaning
              </div>
              <p
                id="review-card-meaning"
                className="text-2xl font-bold text-emerald-400"
              >
                {item.meaningVi}
              </p>
            </div>

            {item.exampleSentence && (
              <div className="bg-surface-800/80 rounded-xl p-3.5 border-l-4 border-primary-500">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  Example
                </div>
                <p id="review-card-example" className="text-sm italic text-slate-200">
                  "{item.exampleSentence}"
                </p>
              </div>
            )}

            {/* Synonyms & Collocations */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              {item.synonyms.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                    Synonyms
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {item.synonyms.map((s, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-xs text-slate-300 font-medium"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {item.collocations.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                    Collocations
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {item.collocations.map((c, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md bg-primary-500/10 border border-primary-500/20 text-xs text-primary-300 font-medium"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Reveal Answer Button */
          <div className="flex flex-col items-center justify-center pt-6 pb-2">
            <button
              type="button"
              id="reveal-answer-btn"
              onClick={(e) => {
                e.stopPropagation();
                onReveal();
              }}
              className="px-8 py-3 rounded-2xl bg-gradient-to-r from-primary-600 to-accent-600 hover:from-primary-500 hover:to-accent-500
                         text-white font-semibold text-sm shadow-xl shadow-primary-900/40 hover:shadow-primary-900/60
                         transition-all transform hover:-translate-y-0.5 active:scale-95 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Show Answer (Space)
            </button>
            <p className="text-xs text-slate-500 mt-3 font-medium">
              Click anywhere on the card or press Space
            </p>
          </div>
        )}
      </div>

      {/* ─── RATING BUTTONS (SHOWN ONLY WHEN REVEALED) ─────────── */}
      {isRevealed && (
        <div id="review-ratings-panel" className="animate-slide-in pt-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Again Button */}
            <button
              type="button"
              id="rate-again-btn"
              disabled={isSubmitting}
              onClick={() => onRate('AGAIN')}
              className="group flex flex-col items-center justify-center p-3.5 rounded-2xl border
                         bg-rose-500/10 border-rose-500/30 hover:bg-rose-500/20 hover:border-rose-500/50
                         transition-all transform active:scale-95 text-rose-300 disabled:opacity-50"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs px-1.5 py-0.5 rounded bg-rose-500/20 font-mono font-bold text-rose-300">
                  1
                </span>
                <span className="text-sm font-bold text-white group-hover:text-rose-200">
                  Again
                </span>
              </div>
              <span className="text-[11px] text-rose-400 font-medium">&lt; 10 min</span>
            </button>

            {/* Hard Button */}
            <button
              type="button"
              id="rate-hard-btn"
              disabled={isSubmitting}
              onClick={() => onRate('HARD')}
              className="group flex flex-col items-center justify-center p-3.5 rounded-2xl border
                         bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20 hover:border-amber-500/50
                         transition-all transform active:scale-95 text-amber-300 disabled:opacity-50"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 font-mono font-bold text-amber-300">
                  2
                </span>
                <span className="text-sm font-bold text-white group-hover:text-amber-200">
                  Hard
                </span>
              </div>
              <span className="text-[11px] text-amber-400 font-medium">+1 day</span>
            </button>

            {/* Good Button */}
            <button
              type="button"
              id="rate-good-btn"
              disabled={isSubmitting}
              onClick={() => onRate('GOOD')}
              className="group flex flex-col items-center justify-center p-3.5 rounded-2xl border
                         bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-500/50
                         transition-all transform active:scale-95 text-emerald-300 disabled:opacity-50"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 font-mono font-bold text-emerald-300">
                  3
                </span>
                <span className="text-sm font-bold text-white group-hover:text-emerald-200">
                  Good
                </span>
              </div>
              <span className="text-[11px] text-emerald-400 font-medium">+3 days</span>
            </button>

            {/* Easy Button */}
            <button
              type="button"
              id="rate-easy-btn"
              disabled={isSubmitting}
              onClick={() => onRate('EASY')}
              className="group flex flex-col items-center justify-center p-3.5 rounded-2xl border
                         bg-sky-500/10 border-sky-500/30 hover:bg-sky-500/20 hover:border-sky-500/50
                         transition-all transform active:scale-95 text-sky-300 disabled:opacity-50"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs px-1.5 py-0.5 rounded bg-sky-500/20 font-mono font-bold text-sky-300">
                  4
                </span>
                <span className="text-sm font-bold text-white group-hover:text-sky-200">
                  Easy
                </span>
              </div>
              <span className="text-[11px] text-sky-400 font-medium">+5 days</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
